import { faQuestion } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { PERIOD_OPTIONS, Period, PeriodProvider, Range } from './period'
import { StatisticEarningFee } from '~/features/bank/components'
import { Box, Header } from '~/shared/ui'
import { Accounts } from '~/widgets'
import { Bank, Connections, Nft, Players, Store } from '~/widgets/statistic'
import { ActivePlayers } from './ActivePlayers'
import { Downloads } from './Downloads'
import { Profit } from './Profit'

export const StatisticEntry = ({
    name,
    value,
    brackets_value,
    title,
}: {
    name: any
    value: number
    brackets_value?: number
    title?: string
}) => {
    return (
        <div className='flex flex-col gap-1'>
            <div className='text-sm flex flex-row gap-1 items-start md:items-center justify-center text-center'>
                <div className='opacity-80 tracking-wide'>{name}</div>
                {title && (
                    // <FontAwesomeIcon title={title} icon={faCircleQuestion} className='bg-white text-black outline-white/80 hover:outline-white rounded-full' />
                    <div
                        title={title}
                        className='flex flex-row items-center justify-center rounded-full border border-white/50 hover:border-white p-2 h-4 w-4 text-[0.7em] bg-[rgba(200,200,200,0.1)] cursor-default'
                    >
                        <FontAwesomeIcon icon={faQuestion} />
                    </div>
                )}
            </div>
            <div className='flex flex-col items-center gap-2 bg-[rgba(200,200,200,0.1)] p-3 rounded group-hover:outline outline-zinc-700'>
                <div className='text-2xl min-h-[2rem] font-bold tracking-wide'>
                    {value} {brackets_value !== undefined && <span>({brackets_value})</span>}
                </div>
            </div>
        </div>
    )
}

export const StatisticRow = (props) => {
    return (
        <section
            className={['flex flex-col items-center justify-center gap-3 pt-4 text-center', props.className]
                .filter(Boolean)
                .join(' ')}
        >
            {props.header && (
                <header className='text-white/80 text-xl tracking-widest font-bold'>{props.header}</header>
            )}
            {props.subheader && <header className='text-white/40 text-xs py-2'>{props.subheader}</header>}
            <div
                className={`grid grid-cols-1 md:grid-cols-2 items-center justify-center gap-4 ${
                    props.gridClassName ?? ''
                }`}
            >
                {props.children}
            </div>
        </section>
    )
}

/** One figure per tab, grouped by subject in the rail.
 *
 *  ONE THING AT A TIME. This was eighteen blocks in a column, then five tabs
 *  of three or four blocks each -- which still meant scrolling past Accounts
 *  and a chart to reach Downloads. A tab that holds four widgets is a shorter
 *  scroll, not an answer. So a tab is now one figure, and the rail is the
 *  whole contents of the page, readable without opening anything.
 *
 *  `group` is the heading it sits under. The groups read in order: who is
 *  here, what they do, what they spend, what they hold, how they connect.
 *
 *  `periods` is whether a window means anything to it. A standing total --
 *  how many tokens exist, how many accounts are linked -- does not move with
 *  the calendar, and a Today control over one would be a control that does
 *  nothing.
 */
type Item = {
    id: string
    label: string
    group: string
    periods: boolean
    /** Offer Live as well: what is happening right now, refreshed. */
    live?: boolean
    /** Leave All time off, for a figure that cannot look back that far. */
    noAll?: boolean
    render: () => any
}

const ITEMS: Item[] = [
    {
        id: 'accounts',
        label: 'Accounts',
        group: 'People',
        periods: true,
        render: () => <Accounts.Count />,
    },
    {
        id: 'active-users',
        label: 'Active site users',
        group: 'People',
        periods: true,
        render: () => <Accounts.ActiveCount />,
    },
    {
        id: 'beta',
        label: 'Beta users',
        group: 'People',
        periods: true,
        render: () => <Accounts.BetaUserCount />,
    },
    {
        id: 'referrals',
        label: 'Referrals',
        group: 'People',
        // Signups by day; link visits (no dates) sit under All time.
        periods: true,
        render: () => <Accounts.Referrals />,
    },
    {
        id: 'downloads',
        label: 'First game logins',
        group: 'People',
        periods: true,
        render: () => <Downloads />,
    },

    {
        id: 'active-players',
        label: 'Active players',
        group: 'Playing',
        periods: true,
        render: () => <ActivePlayers />,
    },
    {
        id: 'concurrent',
        label: 'Concurrent players',
        group: 'Playing',
        // The peak inside the window, and Live for right now. The history only
        // goes back 31 days, so there is no All time.
        periods: true,
        live: true,
        noAll: true,
        render: () => <Players.Concurrent />,
    },

    {
        id: 'profit',
        label: 'Profit',
        group: 'Money',
        periods: true,
        render: () => <Profit />,
    },
    {
        id: 'deposits',
        label: 'Deposits',
        group: 'Money',
        periods: true,
        render: () => <Bank.Deposits />,
    },
    {
        id: 'card',
        label: 'Card payments',
        group: 'Money',
        periods: false,
        render: () => <Bank.Moonpay />,
    },
    {
        id: 'fees',
        label: 'USDC fees kept',
        group: 'Money',
        periods: true,
        render: () => <StatisticEarningFee />,
    },
    {
        id: 'store',
        label: 'Store sales',
        group: 'Money',
        periods: true,
        render: () => <Store.Sales />,
    },

    {
        id: 'tokens',
        label: 'Tokens and holders',
        group: 'NFTs',
        periods: false,
        render: () => (
            <Box>
                <Header>NFTs</Header>
                <section className='grid grid-cols-2'>
                    <Nft.TokensCount />
                    <Nft.HoldersCount />
                </section>
            </Box>
        ),
    },
    {
        id: 'guilds',
        label: 'Guilds',
        group: 'NFTs',
        periods: false,
        render: () => <Nft.GuildsCount />,
    },

    {
        // The four counts are one subject read four ways -- how many accounts
        // are linked at all, then to one, two and three others. Four tabs
        // would be four clicks to answer one question.
        id: 'connections',
        label: 'Linked accounts',
        group: 'Connections',
        periods: false,
        render: () => (
            <>
                <Connections.TotalCount />
                <Connections.OneCount />
                <Connections.TwoCount />
                <Connections.ThreeCount />
            </>
        ),
    },
]

const GROUPS = [...new Set(ITEMS.map((item) => item.group))]

/** The contents of the page, down the left.
 *
 *  Grouped rather than a flat list of fourteen: the headings are what make it
 *  scannable, and they are not clickable because a group is not a view.
 */
const ItemRail = ({ current, onPick }: { current: string; onPick: (id: string) => void }) => (
    <nav className='flex h-fit flex-col gap-4'>
        {GROUPS.map((group) => (
            <div key={group} className='flex flex-col gap-0.5'>
                <span className='pb-1 text-[10px] font-bold uppercase tracking-widest text-white/30'>{group}</span>
                {ITEMS.filter((item) => item.group === group).map((item) => {
                    const on = current === item.id
                    return (
                        <button
                            key={item.id}
                            type='button'
                            onClick={() => onPick(item.id)}
                            className={twMerge(
                                'rounded border-l-2 px-3 py-1 text-left text-xs transition-colors',
                                on
                                    ? 'border-violet-500 bg-white/[0.06] font-bold text-white'
                                    : 'border-transparent text-white/45 hover:bg-white/[0.03] hover:text-white/80'
                            )}
                        >
                            {item.label}
                        </button>
                    )
                })}
            </div>
        ))}
    </nav>
)

/** The window the open figure is being read over.
 *
 *  Per FIGURE rather than one for the page: "today" is a sensible question to
 *  ask of deposits and a strange one to ask of signups, and a single switch
 *  would make every tab answer whichever question the last one was about.
 *
 *  Starts on the month -- the widest of the three, so nothing is hidden until
 *  somebody narrows it deliberately.
 */
const PeriodTabs = ({
    period,
    range,
    onPick,
    onRange,
    live,
    noAll,
}: {
    period: Period
    range: Range
    onPick: (p: Period) => void
    onRange: (r: Range) => void
    live?: boolean
    noAll?: boolean
}) => (
    <span className='ml-auto flex flex-row flex-wrap items-center justify-end gap-1'>
        {[...(live ? ([['live', 'Live']] as [Period, string][]) : []), ...PERIOD_OPTIONS]
            .filter(([key]) => !(noAll && key === 'all'))
            .map(([key, label]) => (
            <button
                key={key}
                type='button'
                onClick={() => onPick(key)}
                className={twMerge(
                    'rounded px-2 py-0.5 text-[10px] uppercase tracking-widest',
                    period === key ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/80'
                )}
            >
                {label}
            </button>
        ))}
        {/* Whole days in the zone picked top right, both ends included. */}
        {period === 'custom' && (
            <span className='flex flex-row items-center gap-1 text-[10px] text-white/50'>
                <input
                    type='date'
                    value={range.from}
                    max={range.to}
                    onChange={(e) => e.target.value && onRange({ ...range, from: e.target.value })}
                    className='rounded border border-white/15 bg-transparent px-1 py-0.5 text-white [color-scheme:dark]'
                />
                to
                <input
                    type='date'
                    value={range.to}
                    min={range.from}
                    onChange={(e) => e.target.value && onRange({ ...range, to: e.target.value })}
                    className='rounded border border-white/15 bg-transparent px-1 py-0.5 text-white [color-scheme:dark]'
                />
            </span>
        )}
    </span>
)

/** The last seven days, today included: where Custom starts. */
const lastWeek = (): Range => {
    const day = (offset: number) => {
        const d = new Date()
        d.setDate(d.getDate() - offset)
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }
    return { from: day(6), to: day(0) }
}

export const Statistic = () => {
    const [open, setOpen] = useState(ITEMS[0].id)
    /* One period per figure, remembered while the page is open. Opening
       Deposits and coming back should not quietly reset Accounts to the month
       after somebody chose today. */
    const [periods, setPeriods] = useState<Record<string, Period>>({})
    const [ranges, setRanges] = useState<Record<string, Range>>({})
    const item = ITEMS.find((i) => i.id === open) ?? ITEMS[0]
    const period = periods[item.id] ?? 'month'
    const range = ranges[item.id] ?? lastWeek()

    return (
        <Box>
            <header className='uppercase font-bold text-center py-2 text-2xl'>Dashboard</header>
            {/* One rule, stated once, because it used to differ figure by
                figure with nothing on screen to say so: tagging an account as
                fraud moved Accounts and left Downloads, Active Players and the
                rest.

                Behind a mark rather than printed in full. It applies to the
                whole page, so above a single figure it was four lines of text
                mostly about the other thirteen -- the largest thing on screen
                and the least likely to be what somebody came for. */}
            <small className='flex flex-row items-center justify-center gap-1.5 pb-2 text-white/40'>
                Days follow the zone chosen top right.
                <span
                    title={
                        'People counts (Accounts, Active Site Users, Downloads, Active Players, Referrals, ' +
                        'Connections) leave out fraud-tagged accounts. Money figures (Deposits, Card payments, ' +
                        'Collected Fees, Store Sales) and Concurrent Players include them: that money moved and ' +
                        'those seats were used.'
                    }
                    className='cursor-help underline decoration-dotted underline-offset-2 hover:text-white'
                >
                    Which counts include fraud-tagged accounts?
                </span>
            </small>
            <Box className='border border-white/10 rounded-lg overflow-hidden py-8'>
                <div className=' bg-custom-gradient-1 h-1 absolute top-0 left-0 w-full'></div>
                <div className='mx-auto grid w-full gap-x-8 gap-y-6 px-4 sm:w-5/6 sm:px-0 lg:grid-cols-[12rem_minmax(0,1fr)]'>
                    <ItemRail current={item.id} onPick={setOpen} />
                    <div className='flex w-full flex-col gap-4 self-start'>
                        <header className='flex flex-row flex-wrap items-center gap-x-3 gap-y-1 border-b border-white/10 pb-1'>
                            <span className='text-[10px] uppercase tracking-widest text-white/30'>{item.group}</span>
                            <h2 className='text-sm font-bold uppercase tracking-widest text-white/70'>{item.label}</h2>
                            {item.periods && (
                                <PeriodTabs
                                    period={period}
                                    range={range}
                                    onPick={(p) => setPeriods((all) => ({ ...all, [item.id]: p }))}
                                    onRange={(r) => setRanges((all) => ({ ...all, [item.id]: r }))}
                                    live={item.live}
                                    noAll={item.noAll}
                                />
                            )}
                        </header>
                        {/* Null for a figure with no window, so it shows what it
                            always showed rather than being narrowed by a control
                            that is not on screen. */}
                        <PeriodProvider value={item.periods ? { period, range } : null}>
                            <Box className='w-full flex-none gap-y-8'>{item.render()}</Box>
                        </PeriodProvider>
                    </div>
                </div>
            </Box>
        </Box>
    )
}
